import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, TextField } from '@mui/material';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Gathering, GatheringStatus } from '../../../types';
import { useGatheringsForDateRange } from '../../gatherings/hooks/data/useGatheringsForDateRange';
import { getWeekRange } from '../../../utils/dateRangeUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import GatheringInfoModal from './GatheringInfoModal';

// Setup the localizer
const localizer = momentLocalizer(moment);

interface GatheringsCalendarProps {
  token: string | null;
  selectedSpaceId?: string;
}

const GatheringsCalendar: React.FC<GatheringsCalendarProps> = ({ 
  token,
  selectedSpaceId
}) => {
  // Manage current date internally
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  // State for gathering info modal
  const [selectedGatheringId, setSelectedGatheringId] = useState<string | null>(null);
  
  // Calculate week date range (starting on Monday) - memoize to prevent recalculation on every render
  const dateRange = useMemo(() => {
    return getWeekRange(currentDate, 1); // 1 = Monday
  }, [currentDate]);
  
  const [startDate, endDate] = dateRange;
  
  // Use the core date range hook directly
  const { 
    gatherings: allWeekGatherings, 
    isLoading, 
    isBackgroundLoading,
    error, 
    refresh 
  } = useGatheringsForDateRange(startDate, endDate, token);
  
  // Apply space filter to gatherings - memoize to prevent recalculation on every render
  const weekGatherings = useMemo(() => {
    return allWeekGatherings.filter(gathering => {
      // Apply space filter if selected
      if (selectedSpaceId && gathering.space?.id !== selectedSpaceId) {
        return false;
      }
      // Exclude denied gatherings from Space Ops calendar
      if (gathering.status === GatheringStatus.DENIED) {
        return false;
      }
      // Show both published and unpublished gatherings (except denied)
      return true;
    });
  }, [allWeekGatherings, selectedSpaceId]);
  
  // Navigation handlers
  const handlePrevWeek = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };
  
  const handleNextWeek = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  // Handle date picker change - jump to selected week
  const handleDatePickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = new Date(event.target.value);
    if (!isNaN(selectedDate.getTime())) {
      setCurrentDate(selectedDate);
    }
  };
  
  // Format current date for date picker input (YYYY-MM-DD format)
  const formatDateForInput = (date: Date): string => {
    return moment(date).format('YYYY-MM-DD');
  };
  
  // Convert gatherings to events for the calendar
  const events = useMemo(() => {
    return weekGatherings.map(gathering => {
      // Extract date part from the ISO string
      const dateStr = gathering.date;
      
      // Parse start and end times correctly
      let startTime = gathering.start_time;
      let endTime = gathering.end_time;
      
      // Create date objects for start and end
      const startDate = moment(`${dateStr} ${startTime}`, 'YYYY-MM-DD hh:mm a').toDate();
      const endDate = moment(`${dateStr} ${endTime}`, 'YYYY-MM-DD hh:mm a').toDate();
      
      return {
        id: gathering.id,
        title: gathering.title,
        start: startDate,
        end: endDate,
        status: gathering.status,
        space: gathering.space?.name,
        allDay: false,
        resource: gathering // Store full gathering data for tooltips
      };
    });
  }, [weekGatherings]);
  
  // Style events based on status - using consistent colors from the app
  const eventStyleGetter = (event: any) => {
    let backgroundColor = '#95a5a6'; // default light gray
    
    switch(event.status) {
      case GatheringStatus.NEW:
        backgroundColor = '#3498db'; // Blue
        break;
      case GatheringStatus.IN_REVIEW:
        backgroundColor = '#f39c12'; // Orange
        break;
      case GatheringStatus.NEED_MORE_INFO:
        backgroundColor = '#e74c3c'; // Red
        break;
      case GatheringStatus.RESCHEDULE:
        backgroundColor = '#9b59b6'; // Purple
        break;
      case GatheringStatus.AWAITING_GATHERING_LINK:
        backgroundColor = '#2ecc71'; // Green
        break;
      case GatheringStatus.READY_TO_PUBLISH:
        backgroundColor = '#1abc9c'; // Teal
        break;
      case GatheringStatus.PUBLISHED:
        backgroundColor = '#27ae60'; // Dark Green
        break;
      case GatheringStatus.DENIED:
        backgroundColor = '#7f8c8d'; // Gray
        break;
    }
    
    const style = {
      backgroundColor,
      color: '#fff',
      borderRadius: '3px',
      border: '1px solid rgba(0, 0, 0, 0.2)', // Darker border for better visibility
      display: 'block',
      fontSize: '0.75rem', // Smaller font for compact view
      padding: '2px 4px'
    };
    return {
      style
    };
  };

  // Custom event component for basic info display
  const EventComponent = ({ event }: { event: any }) => (
    <Tooltip 
      title={
        <div>
          <div><strong>{event.title}</strong></div>
          <div>Status: {event.status}</div>
          {event.space && <div>Space: {event.space}</div>}
          <div>Time: {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}</div>
          <div style={{ fontSize: '0.8rem', marginTop: '4px', fontStyle: 'italic' }}>
            Click for details
          </div>
        </div>
      }
      arrow
    >
      <div style={{ fontSize: '0.7rem', lineHeight: '1.1' }}>
        {event.title}
      </div>
    </Tooltip>
  );
  
  // Handle event click to open modal
  const handleEventClick = (event: any) => {
    setSelectedGatheringId(event.id);
  };
  
  // Handle modal close
  const handleModalClose = () => {
    setSelectedGatheringId(null);
  };
  
  return (
    <Box className="gatherings-calendar-compact">
      {/* Compact Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
          {moment(startDate).format('MMM D')} - {moment(endDate).format('MMM D, YYYY')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton onClick={handlePrevWeek} size="small" sx={{ padding: '2px' }}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <TextField
            type="date"
            value={formatDateForInput(currentDate)}
            onChange={handleDatePickerChange}
            size="small"
            sx={{ width: 120, fontSize: '0.75rem' }}
          />
          <IconButton onClick={handleToday} size="small" sx={{ padding: '2px' }}>
            <Today fontSize="small" />
          </IconButton>
          <IconButton onClick={handleNextWeek} size="small" sx={{ padding: '2px' }}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      
      {/* Background loading indicator */}
      {isBackgroundLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: '5px', 
          right: '5px',
          zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.9)',
          padding: '2px 6px',
          borderRadius: '3px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '0.7rem'
        }}>
          <div className="spinner-small"></div>
          <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>Refreshing...</Typography>
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Box sx={{ color: 'error.main', mb: 1, textAlign: 'center', fontSize: '0.8rem' }}>
          {error}
          <button onClick={refresh} className="small-button" style={{ marginLeft: '8px', fontSize: '0.7rem' }}>
            Retry
          </button>
        </Box>
      )}
      
      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div className="spinner"></div>
        </Box>
      ) : (
        <Box sx={{ height: 'calc(100% - 80px)', '& .rbc-calendar': { fontSize: '0.75rem' } }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            defaultView={Views.WEEK}
            date={currentDate}
            onNavigate={date => setCurrentDate(date)}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventClick}
            views={['week']}
            min={moment().hours(8).minutes(0).seconds(0).toDate()} // Start at 8:00 AM
            max={moment().hours(21).minutes(0).seconds(0).toDate()} // End at 9:00 PM
            components={{
              event: EventComponent
            }}
            toolbar={false} // Hide default toolbar since we have custom navigation
            popup={false} // Disable popup, use tooltips instead
          />
        </Box>
      )}
      
      {/* Status Legend - Compact */}
      {events.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, fontSize: '0.65rem' }}>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 'bold', mr: 0.5 }}>
            Status:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Box sx={{ width: 8, height: 8, backgroundColor: '#27ae60', borderRadius: 0.5 }}></Box>
            <span>Published</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Box sx={{ width: 8, height: 8, backgroundColor: '#1abc9c', borderRadius: 0.5 }}></Box>
            <span>Ready</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Box sx={{ width: 8, height: 8, backgroundColor: '#3498db', borderRadius: 0.5 }}></Box>
            <span>New</span>
          </Box>
        </Box>
      )}
      
      {/* Gathering Info Modal */}
      <GatheringInfoModal
        gatheringId={selectedGatheringId}
        isOpen={selectedGatheringId !== null}
        onClose={handleModalClose}
        token={token}
      />
    </Box>
  );
};

export default GatheringsCalendar;
